import { z } from 'zod';
import { Uuid } from './schemas';

/* ───────────── Журнал изменений: просмотр, отмена, восстановление, проверка базы (ДС-11, НФ-12, НФ-21) ─────────────
 *
 * Единица отмены — отправка (одна кнопка «Сохранить», «Удалить», «Провести»): всё, что портал изменил одним
 * запросом, возвращается вместе — запись, итоги, связанные строки. Так база остаётся согласованной. Если записи
 * отправки после неё меняли другие отправки, отменить можно только вместе с ними («зависимые отправки»).
 * Перед отменой — пробный прогон в базе: что изменится (с каскадом), что мешает, что нужно подтвердить.
 */

/** Разделы журнала: отбор и подписи */
export const JOURNAL_SECTIONS = {
  projects: 'Проекты и поручения',
  counterparties: 'Контрагенты',
  procurement: 'Закупки',
  contracts: 'Договоры',
  finance: 'Финансы и оплаты',
  estimates: 'Сметы и бюджет',
  schedule: 'График и ресурсы',
  psd: 'ПСД, надзор и изменения',
  quality: 'Качество, ОТ и документы',
  site: 'Площадка',
  warehouse: 'Склад',
  fleet: 'Техника',
  video: 'Видеонаблюдение',
  kb: 'База знаний',
  analytics: 'Отчёты и виды',
  support: 'Поддержка и предложения',
  integrations: 'Обмен с системами',
  files: 'Файлы',
  admin: 'Администрирование',
  system: 'Служебные записи',
} as const;
export type JournalSection = keyof typeof JOURNAL_SECTIONS;
export const JournalSectionCode = z.enum(
  Object.keys(JOURNAL_SECTIONS) as [JournalSection, ...JournalSection[]],
);

/** Таблицы журнала: название записи (в именительном падеже) и раздел. Ключ — «схема.таблица» */
export const JOURNAL_TABLES: Record<string, { title: string; section: JournalSection }> = {
  // Отчёты и виды страниц
  'an.digest_prefs': { title: 'Подписка на сводку руководителю', section: 'analytics' },
  'an.list_views': { title: 'Вид списка', section: 'analytics' },
  'an.report_runs': { title: 'Выпуск отчёта', section: 'analytics' },
  'an.reports': { title: 'Отчёт конструктора', section: 'analytics' },
  'an.view_prefs': { title: 'Выбранный вид страницы', section: 'analytics' },
  // Администрирование и общее
  'core.attachments': { title: 'Вложение', section: 'files' },
  'core.files': { title: 'Файл', section: 'files' },
  'core.command_receipts': { title: 'Квитанция команды', section: 'system' },
  'core.outbox_events': { title: 'Событие для других разделов', section: 'system' },
  'core.event_inbox': { title: 'Принятое событие', section: 'system' },
  'core.event_replays': { title: 'Повтор события', section: 'system' },
  'core.event_deliveries': { title: 'Доставка события', section: 'system' },
  'core.delegations': { title: 'Замещение', section: 'admin' },
  'core.discussion_messages': { title: 'Сообщение обсуждения', section: 'projects' },
  'core.entity_links': { title: 'Связь записей', section: 'projects' },
  'core.invites': { title: 'Приглашение', section: 'admin' },
  'core.legal_entities': { title: 'Юрлицо', section: 'admin' },
  'core.licenses': { title: 'Лицензия', section: 'admin' },
  'core.memberships': { title: 'Сотрудник (доступ)', section: 'admin' },
  'core.org_units': { title: 'Подразделение', section: 'admin' },
  'core.privacy_requests': { title: 'Запрос по персональным данным', section: 'admin' },
  'core.role_assignments': { title: 'Назначение роли', section: 'admin' },
  'core.roles': { title: 'Роль', section: 'admin' },
  'core.staff': { title: 'Сотрудник (справочник)', section: 'admin' },
  'core.tenant_settings': { title: 'Настройка компании', section: 'admin' },
  'core.tenants': { title: 'Компания', section: 'admin' },
  'core.users': { title: 'Учётная запись', section: 'admin' },
  // Договоры
  'ctr.amendment_approvals': { title: 'Согласование допсоглашения', section: 'contracts' },
  'ctr.amendments': { title: 'Допсоглашение', section: 'contracts' },
  'ctr.approvals': { title: 'Согласование договора', section: 'contracts' },
  'ctr.contract_versions': { title: 'Версия договора', section: 'contracts' },
  'ctr.contracts': { title: 'Договор', section: 'contracts' },
  'ctr.items': { title: 'Позиция спецификации договора', section: 'contracts' },
  'ctr.schedule': { title: 'Строка графика договора', section: 'contracts' },
  'ctr.obligations': { title: 'Обязательство по договору', section: 'contracts' },
  'ctr.securities': { title: 'Обеспечение по договору', section: 'contracts' },
  // Сметы, нормативная база, бюджет
  'est.budget_changes': { title: 'Изменение утверждённого бюджета', section: 'estimates' },
  'est.cost_variants': { title: 'Вариант исполнения раздела', section: 'estimates' },
  'est.estimate_item_coefficients': { title: 'Коэффициент к позиции сметы', section: 'estimates' },
  'est.estimate_item_comments': { title: 'Комментарий к позиции сметы', section: 'estimates' },
  'est.estimate_item_resources': { title: 'Ресурс позиции сметы', section: 'estimates' },
  'est.estimate_items': { title: 'Позиция сметы', section: 'estimates' },
  'est.estimate_sections': { title: 'Раздел сметы', section: 'estimates' },
  'est.estimates': { title: 'Смета', section: 'estimates' },
  'est.library_imports': { title: 'Загрузка нормативной библиотеки', section: 'estimates' },
  'est.library_reviews': { title: 'Проверка нормативной библиотеки', section: 'estimates' },
  'est.norm_item_resources': { title: 'Ресурс нормы', section: 'estimates' },
  'est.norm_items': { title: 'Норма (расценка)', section: 'estimates' },
  'est.norm_releases': { title: 'Выпуск нормативной базы', section: 'estimates' },
  'est.norm_resources': { title: 'Ресурс нормативной базы', section: 'estimates' },
  'est.project_prices': { title: 'Плановая цена проекта', section: 'estimates' },
  'est.site_cost_entries': { title: 'Факт затрат площадки', section: 'estimates' },
  'est.site_cost_receipts': { title: 'Приём затрат площадки', section: 'estimates' },
  'est.site_cost_revisions': { title: 'Ревизия затрат площадки', section: 'estimates' },
  'est.site_cost_valuations': { title: 'Оценка затрат площадки', section: 'estimates' },
  'est.site_costs': { title: 'Затраты площадки и АУП', section: 'estimates' },
  'pm.budget_lines': { title: 'Раздел бюджета', section: 'estimates' },
  'pm.budget_versions': { title: 'Версия бюджета', section: 'estimates' },
  // Финансы
  'fin.advance_offsets': { title: 'Зачёт аванса', section: 'finance' },
  'fin.advances': { title: 'Аванс', section: 'finance' },
  'fin.approval_limits': { title: 'Лимит согласования', section: 'finance' },
  'fin.cash_covers': { title: 'Покрытие кассового разрыва', section: 'finance' },
  'fin.cost_articles': { title: 'Статья расходов', section: 'finance' },
  'fin.export_batches': { title: 'Выгрузка в 1С', section: 'finance' },
  'fin.export_profiles': { title: 'Профиль выгрузки в 1С', section: 'finance' },
  'fin.payment_doc_rules': { title: 'Комплект документов к оплате', section: 'finance' },
  'fin.payment_invoice_lines': { title: 'Позиция счёта к оплате', section: 'finance' },
  'fin.payment_moves': { title: 'Перенос платежа', section: 'finance' },
  'fin.payment_requests': { title: 'Заявка на оплату', section: 'finance' },
  'fin.plan_rows': { title: 'Строка финансового плана', section: 'finance' },
  'fin.plans': { title: 'Финансовый план', section: 'finance' },
  'fin.retentions': { title: 'Гарантийное удержание', section: 'finance' },
  'fin.money_payments': { title: 'Платёж из money-портала', section: 'finance' },
  'pm.payment_facts': { title: 'Оплата', section: 'finance' },
  'pm.financing': { title: 'Условия финансирования', section: 'finance' },
  // Техника
  'fleet.cost_postings': { title: 'Списание машино-часов', section: 'fleet' },
  'fleet.fuel_fills': { title: 'Заправка', section: 'fleet' },
  'fleet.output_norms': { title: 'Норма выработки', section: 'fleet' },
  'fleet.unit_cards': { title: 'Карточка техники', section: 'fleet' },
  'fleet.unit_docs': { title: 'Документ техники', section: 'fleet' },
  'fleet.waybills': { title: 'Путевой лист', section: 'fleet' },
  'fleet.work_orders': { title: 'Сменный наряд на технику', section: 'fleet' },
  'fleet.parts_requests': { title: 'Заявка на запчасти к ТО', section: 'fleet' },
  'fleet.service_marks': { title: 'Отметка ТО техники', section: 'fleet' },
  'fleet.service_rules': { title: 'Регламент ТО техники', section: 'fleet' },
  // Обмен с системами
  'integ.connectors': { title: 'Коннектор обмена', section: 'integrations' },
  'integ.field_maps': { title: 'Сопоставление полей обмена', section: 'integrations' },
  'integ.unmapped': { title: 'Значение обмена без привязки', section: 'integrations' },
  // База знаний
  'kb.article_links': { title: 'Связь статьи', section: 'kb' },
  'kb.article_versions': { title: 'Версия статьи', section: 'kb' },
  'kb.articles': { title: 'Статья базы знаний', section: 'kb' },
  'kb.subscription_events': { title: 'Событие подписки', section: 'kb' },
  'kb.subscriptions': { title: 'Подписка на изменения', section: 'kb' },
  'kb.field_bases': { title: 'Регламент — основание срока', section: 'kb' },
  // График и ресурсы
  'pl.activities': { title: 'Работа графика', section: 'schedule' },
  'pl.activity_codes': { title: 'Код работы графика', section: 'schedule' },
  'pl.activity_steps': { title: 'Шаг работы графика', section: 'schedule' },
  'pl.activity_workers': { title: 'Исполнитель работы', section: 'schedule' },
  'pl.assignments': { title: 'Назначение ресурса', section: 'schedule' },
  'pl.baselines': { title: 'Базовый план', section: 'schedule' },
  'pl.calendars': { title: 'Календарь', section: 'schedule' },
  'pl.camp_stays': { title: 'Проживание по вахте', section: 'schedule' },
  'pl.camp_units': { title: 'Вахтовый посёлок', section: 'schedule' },
  'pl.code_types': { title: 'Вид кода работ', section: 'schedule' },
  'pl.code_values': { title: 'Значение кода работ', section: 'schedule' },
  'pl.commitments': { title: 'Обязательство недельного плана', section: 'schedule' },
  'pl.contractor_updates': { title: 'Отчёт подрядчика по работе', section: 'schedule' },
  'pl.fragment_templates': { title: 'Шаблон фрагмента графика', section: 'schedule' },
  'pl.machine_logs': { title: 'Учёт работы машины за день', section: 'schedule' },
  'pl.machine_services': { title: 'ТО и ремонт машины', section: 'schedule' },
  'pl.machine_telemetry': { title: 'Телематика техники', section: 'schedule' },
  'pl.machines': { title: 'Машина парка', section: 'schedule' },
  'pl.mobilizations': { title: 'План мобилизации', section: 'schedule' },
  'pl.package_constraints': { title: 'Ограничение пакета работ', section: 'schedule' },
  'pl.package_items': { title: 'Работа пакета', section: 'schedule' },
  'pl.progress_rules': { title: 'Правило начисления выполнения', section: 'schedule' },
  'pl.relations': { title: 'Связь работ', section: 'schedule' },
  'pl.resource_rates': { title: 'Ставка ресурса', section: 'schedule' },
  'pl.resources': { title: 'Ресурс', section: 'schedule' },
  'pl.rotations': { title: 'Вахта', section: 'schedule' },
  'pl.saved_views': { title: 'Сохранённый вид графика', section: 'schedule' },
  'pl.scenarios': { title: 'Сценарий «что если»', section: 'schedule' },
  'pl.schedules': { title: 'График проекта', section: 'schedule' },
  'pl.site_acceptances': { title: 'Принятое основание графика', section: 'schedule' },
  'pl.site_quantity_entries': { title: 'Принятый объём с площадки', section: 'schedule' },
  'pl.timesheet_approvals': { title: 'Согласование табеля', section: 'schedule' },
  'pl.timesheet_entries': { title: 'Строка табеля', section: 'schedule' },
  'pl.wbs': { title: 'Узел ИСР', section: 'schedule' },
  'pl.work_packages': { title: 'Пакет работ', section: 'schedule' },
  'pl.worker_docs': { title: 'Допуск работника', section: 'schedule' },
  'pl.workers': { title: 'Работник', section: 'schedule' },
  'pl.zones': { title: 'Захватка', section: 'schedule' },
  // Проекты
  'pm.attention_items': { title: 'Находка «Требует внимания»', section: 'projects' },
  'pm.card_rows': { title: 'Строка карточки проекта', section: 'projects' },
  'pm.directions': { title: 'Направление', section: 'projects' },
  'pm.milestones': { title: 'Веха', section: 'projects' },
  'pm.precontract': { title: 'Преддоговорная стадия', section: 'projects' },
  'pm.project_objects': { title: 'Объект проекта', section: 'projects' },
  'pm.projects': { title: 'Проект', section: 'projects' },
  'pm.tasks': { title: 'Поручение', section: 'projects' },
  // Контрагенты
  'pm.counterparties': { title: 'Контрагент', section: 'counterparties' },
  'pm.counterparty_checks': { title: 'Проверка контрагента', section: 'counterparties' },
  'pm.counterparty_contacts': { title: 'Представитель контрагента', section: 'counterparties' },
  'pm.counterparty_docs': { title: 'Документ контрагента', section: 'counterparties' },
  'pm.counterparty_stoplist': { title: 'Решение по стоп-листу', section: 'counterparties' },
  // Закупки
  'proc.offer_lines': { title: 'Цена КП по позиции', section: 'procurement' },
  'proc.offers': { title: 'Коммерческое предложение', section: 'procurement' },
  'proc.registries': { title: 'Реестр закупок', section: 'procurement' },
  'proc.request_lines': { title: 'Позиция заявки на закуп', section: 'procurement' },
  'proc.requests': { title: 'Заявка на закуп', section: 'procurement' },
  'proc.rfq_invites': { title: 'Приглашение в запрос КП', section: 'procurement' },
  'proc.rfq_lines': { title: 'Позиция запроса КП', section: 'procurement' },
  'proc.rfq_messages': { title: 'Сообщение запроса КП', section: 'procurement' },
  'proc.rfq_offer_files': { title: 'Файл ответа поставщика', section: 'procurement' },
  'proc.rfq_requests': { title: 'Заявка в запросе КП', section: 'procurement' },
  'proc.rfqs': { title: 'Запрос КП', section: 'procurement' },
  // ПСД, надзор, изменения
  'psd.change_approvals': { title: 'Согласование изменения', section: 'psd' },
  'psd.change_events': { title: 'Ход запроса на изменение', section: 'psd' },
  'psd.change_items': { title: 'Что затрагивает изменение', section: 'psd' },
  'psd.change_settings': { title: 'Лимиты изменений', section: 'psd' },
  'psd.changes': { title: 'Запрос на изменение', section: 'psd' },
  'psd.code_rules': { title: 'Правило шифра комплекта', section: 'psd' },
  'psd.distributions': { title: 'Рассылка ревизии', section: 'psd' },
  'psd.file_texts': { title: 'Текст файла для поиска', section: 'psd' },
  'psd.inspection_events': { title: 'Ход освидетельствования', section: 'psd' },
  'psd.inspection_people': { title: 'Участник освидетельствования', section: 'psd' },
  'psd.inspections': { title: 'Освидетельствование скрытых работ', section: 'psd' },
  'psd.item_files': { title: 'Фото к замечанию или вопросу', section: 'psd' },
  'psd.markups': { title: 'Пометка на листе', section: 'psd' },
  'psd.question_events': { title: 'Ход вопроса к проекту', section: 'psd' },
  'psd.question_reminders': { title: 'Напоминание по вопросу', section: 'psd' },
  'psd.question_reviewers': { title: 'Рецензент вопроса', section: 'psd' },
  'psd.questions': { title: 'Вопрос к проекту', section: 'psd' },
  'psd.remark_events': { title: 'Ход замечания', section: 'psd' },
  'psd.remarks': { title: 'Замечание надзора', section: 'psd' },
  'psd.review_assignments': { title: 'Поручение проверяющему', section: 'psd' },
  'psd.review_routes': { title: 'Маршрут проверки комплекта', section: 'psd' },
  'psd.review_step_people': { title: 'Проверяющий на шаге', section: 'psd' },
  'psd.review_steps': { title: 'Шаг проверки комплекта', section: 'psd' },
  'psd.revision_events': { title: 'Ход ревизии', section: 'psd' },
  'psd.revision_files': { title: 'Файл ревизии', section: 'psd' },
  'psd.revision_reviews': { title: 'Проверка ревизии', section: 'psd' },
  'psd.revision_sheets': { title: 'Лист ревизии', section: 'psd' },
  'psd.revisions': { title: 'Ревизия комплекта', section: 'psd' },
  'psd.sets': { title: 'Комплект ПСД', section: 'psd' },
  'psd.sheet_reviews': { title: 'Решение по листу', section: 'psd' },
  'psd.sheets': { title: 'Лист комплекта', section: 'psd' },
  'psd.transmittal_items': { title: 'Позиция накладной', section: 'psd' },
  'psd.transmittal_links': { title: 'Ссылка на накладную', section: 'psd' },
  'psd.transmittals': { title: 'Накладная на документацию', section: 'psd' },
  'psd.uploads': { title: 'Загрузка файла частями', section: 'psd' },
  'psd.visit_people': { title: 'Участник посещения', section: 'psd' },
  'psd.visits': { title: 'Посещение надзора', section: 'psd' },
  // Качество, ОТ, документы
  'qa.briefing_attendees': { title: 'Участник инструктажа', section: 'quality' },
  'qa.briefings': { title: 'Инструктаж', section: 'quality' },
  'qa.checklist_answers': { title: 'Пункт приёмки по чек-листу', section: 'quality' },
  'qa.checklist_inspections': { title: 'Приёмка по чек-листу', section: 'quality' },
  'qa.checklist_template_items': { title: 'Пункт шаблона чек-листа', section: 'quality' },
  'qa.checklist_templates': { title: 'Шаблон чек-листа', section: 'quality' },
  'qa.exec_doc_items': { title: 'Исполнительный документ', section: 'quality' },
  'qa.exec_doc_template_items': { title: 'Пункт шаблона исполнительной документации', section: 'quality' },
  'qa.exec_doc_templates': { title: 'Шаблон исполнительной документации', section: 'quality' },
  'qa.exec_transmittals': { title: 'Передача исполнительной документации', section: 'quality' },
  'qa.letters': { title: 'Письмо', section: 'quality' },
  'qa.safety_observation_events': { title: 'Ход наблюдения по безопасности', section: 'quality' },
  'qa.site_remark_links': { title: 'Связь замечания с отчётом смены', section: 'quality' },
  'qa.work_permit_crew': { title: 'Бригада наряда-допуска', section: 'quality' },
  'qa.work_permit_signs': { title: 'Подпись наряда-допуска', section: 'quality' },
  'qa.work_permits': { title: 'Наряд-допуск', section: 'quality' },
  'qa.worker_credentials': { title: 'Удостоверение работника', section: 'quality' },
  // Площадка
  'site.delivery_acceptances': { title: 'Приёмка материала на площадке', section: 'site' },
  'site.extra_work_acts': { title: 'Акт на дополнительные работы', section: 'site' },
  'site.report_lines': { title: 'Строка отчёта смены', section: 'site' },
  'site.report_version_lines': { title: 'Объём в версии отчёта', section: 'site' },
  'site.report_versions': { title: 'Версия отчёта смены', section: 'site' },
  'site.reports': { title: 'Отчёт смены', section: 'site' },
  // Поддержка
  'support.reward_awards': { title: 'Премия', section: 'support' },
  'support.reward_periods': { title: 'Итоги премирования', section: 'support' },
  'support.suggestion_events': { title: 'Ход предложения', section: 'support' },
  'support.suggestions': { title: 'Предложение по улучшению', section: 'support' },
  'support.ticket_messages': { title: 'Сообщение в обращении', section: 'support' },
  'support.tickets': { title: 'Обращение в поддержку', section: 'support' },
  // Видео
  'video.biometric_consents': { title: 'Согласие на биометрию', section: 'video' },
  'video.camera_events': { title: 'Событие видеоаналитики', section: 'video' },
  'video.camera_view_log': { title: 'Просмотр видео', section: 'video' },
  'video.cameras': { title: 'Камера', section: 'video' },
  'video.video_settings': { title: 'Настройки видеонаблюдения', section: 'video' },
  // Склад
  'wh.catalog_items': { title: 'Позиция каталога ТМЦ', section: 'warehouse' },
  'wh.codes_1c': { title: 'Код 1С', section: 'warehouse' },
  'wh.inventories': { title: 'Инвентаризация', section: 'warehouse' },
  'wh.inventory_lines': { title: 'Строка инвентаризации', section: 'warehouse' },
  'wh.powers_of_attorney': { title: 'Доверенность', section: 'warehouse' },
  'wh.ppe_issues': { title: 'Выдача СИЗ', section: 'warehouse' },
  'wh.ppe_norms': { title: 'Норма выдачи СИЗ', section: 'warehouse' },
  'wh.preservation_rules': { title: 'Правило консервации', section: 'warehouse' },
  'wh.preservation_works': { title: 'Работа по консервации', section: 'warehouse' },
  'wh.stock_document_lines': { title: 'Строка документа склада', section: 'warehouse' },
  'wh.stock_documents': { title: 'Документ склада', section: 'warehouse' },
  'wh.stock_movements': { title: 'Движение по складу', section: 'warehouse' },
  'wh.stock_reservations': { title: 'Резерв склада', section: 'warehouse' },
  'wh.stock_snapshots': { title: 'Снимок остатков', section: 'warehouse' },
  'wh.substitutions': { title: 'Замена позиции', section: 'warehouse' },
  'wh.tool_events': { title: 'Событие инструмента', section: 'warehouse' },
  'wh.tool_repairs': { title: 'Ремонт инструмента', section: 'warehouse' },
  'wh.tool_units': { title: 'Экземпляр инструмента', section: 'warehouse' },
  'wh.warehouses': { title: 'Склад', section: 'warehouse' },
  // Таблицы, появившиеся после первой редакции журнала
  'ai.usage_log': { title: 'Обращение к Pilot', section: 'system' },
  'core.license_invoices': { title: 'Счёт за лицензию', section: 'admin' },
  'core.license_payments': { title: 'Оплата лицензии', section: 'admin' },
  'core.notification_deliveries': { title: 'Доставка оповещения', section: 'system' },
  'core.platform_events': { title: 'Событие кабинета владельца портала', section: 'system' },
  'core.view_as_log': { title: 'Просмотр «глазами сотрудника»', section: 'admin' },
  'core.backup_places': { title: 'Место хранения резервных копий', section: 'admin' },
  'core.backup_place_secrets': { title: 'Ключ места хранения копий', section: 'system' },
  'core.backup_keys': { title: 'Ключ шифрования копий', section: 'system' },
  'core.backup_runs': { title: 'Резервная копия', section: 'system' },
  'core.backup_files': { title: 'Файл в резервной копии', section: 'system' },
  'core.password_resets': { title: 'Восстановление пароля', section: 'system' },
  'core.money_scopes': { title: 'Уровень денег на проекте', section: 'admin' },
  'est.agsk_entries': { title: 'Позиция классификатора АГСК', section: 'estimates' },
  'est.library_import_rows': { title: 'Строка загрузки нормативной базы', section: 'estimates' },
  'integ.connector_secrets': { title: 'Ключи подключения', section: 'integrations' },
  'integ.exchange_log': { title: 'Журнал обмена', section: 'integrations' },
  'integ.queue': { title: 'Очередь обмена', section: 'integrations' },
  'pl.activity_dates': { title: 'Даты работы графика', section: 'schedule' },
  'pl.activity_locks': { title: 'Блокировка работы графика', section: 'schedule' },
  'pl.baseline_activities': { title: 'Работа базового плана', section: 'schedule' },
  'pl.schedule_changes': { title: 'Правка графика', section: 'schedule' },
  'pm.budget_line_periods': { title: 'Строка бюджета по периодам', section: 'estimates' },
  'proc.request_events': { title: 'Событие заявки на закуп', section: 'procurement' },
  'psd.access_log': { title: 'Просмотр листа ПСД', section: 'psd' },
  'qa.safety_observations': { title: 'Наблюдение по охране труда', section: 'quality' },
  'qa.work_permit_events': { title: 'Событие наряда-допуска', section: 'quality' },
  'qa.work_permit_gas_tests': { title: 'Анализ воздуха по наряду-допуску', section: 'quality' },
  'qa.work_permit_isolations': { title: 'Изоляция LOTO по наряду-допуску', section: 'quality' },
  'qa.work_permit_risks': { title: 'Анализ рисков по наряду-допуску', section: 'quality' },
  'support.suggestion_vote_log': { title: 'Голос за предложение (журнал баллов)', section: 'support' },
  'support.suggestion_votes': { title: 'Голос за предложение', section: 'support' },
  'support.user_help_state': { title: 'Экскурсия по порталу', section: 'support' },
  'video.ingest_keys': { title: 'Ключ видеосервера', section: 'video' },
  'wh.dict_1c': { title: 'Справочник 1С склада', section: 'warehouse' },
};

const SCHEMA_SECTIONS: Record<string, JournalSection> = {
  an: 'analytics',
  ctr: 'contracts',
  est: 'estimates',
  fin: 'finance',
  fleet: 'fleet',
  integ: 'integrations',
  kb: 'kb',
  pl: 'schedule',
  pm: 'projects',
  proc: 'procurement',
  psd: 'psd',
  qa: 'quality',
  site: 'site',
  support: 'support',
  video: 'video',
  wh: 'warehouse',
  core: 'admin',
};

/** Название и раздел таблицы; для новой таблицы без записи — раздел по схеме, название — имя таблицы */
export function journalTable(schema: string, table: string): { title: string; section: JournalSection } {
  const known = JOURNAL_TABLES[`${schema}.${table}`];
  if (known) return known;
  return { title: table.replaceAll('_', ' '), section: SCHEMA_SECTIONS[schema] ?? 'system' };
}

/** Подписи полей словами — в отдельном файле: словарь большой */
export { humanizeField, JOURNAL_FIELD_TITLES, journalFieldTitle } from './datalog-fields';

/* ───────────────────────── Ответы и запросы ───────────────────────── */

export const JournalActor = z.object({ id: Uuid, fullName: z.string() }).nullable();
export type JournalActor = z.infer<typeof JournalActor>;

export const JournalChange = z.object({
  field: z.string(),
  /** Название поля словами: по словарю, по связанной записи или по частям имени */
  title: z.string().optional(),
  before: z.unknown(),
  after: z.unknown(),
  /** Подпись связанной записи вместо номера: проект, договор, сотрудник */
  beforeLabel: z.string().nullable().optional(),
  afterLabel: z.string().nullable().optional(),
  /** Значение скрыто: нет доступа к суммам или личная запись */
  hidden: z.boolean().optional(),
});
export type JournalChange = z.infer<typeof JournalChange>;

export const JournalOp = z.enum(['I', 'U', 'D']);
export type JournalOp = z.infer<typeof JournalOp>;

export const JournalEntry = z.object({
  id: z.string(),
  at: z.string(),
  op: JournalOp,
  schema: z.string(),
  table: z.string(),
  tableTitle: z.string(),
  section: JournalSectionCode,
  rowId: z.string().nullable(),
  recordLabel: z.string().nullable(),
  actor: JournalActor,
  /** Отправка, которой сделано изменение; null — служебная запись без автора */
  actionKey: z.string().nullable(),
  changes: z.array(JournalChange),
  /** Изменение отменено действующей отменой */
  reverted: z.boolean(),
  /** Курсор следующей страницы списка (передаётся в запрос как cursor) */
  cursor: z.string().optional(),
});
export type JournalEntry = z.infer<typeof JournalEntry>;

export const JournalActionState = z.enum(['active', 'reverted', 'partly']);
export type JournalActionState = z.infer<typeof JournalActionState>;

export const JournalAction = z.object({
  /** Ключ отправки для отмены и подробностей (непрозрачная строка) */
  key: z.string(),
  at: z.string(),
  startedAt: z.string(),
  actor: JournalActor,
  /** Что сделано: «Изменение — Договор ДС-01-П-003» */
  title: z.string(),
  route: z.string().nullable(),
  method: z.string().nullable(),
  ip: z.string().nullable(),
  device: z.string().nullable(),
  httpStatus: z.number().nullable(),
  counts: z.object({ created: z.number(), updated: z.number(), deleted: z.number() }),
  sections: z.array(JournalSectionCode),
  records: z.array(z.object({ tableTitle: z.string(), label: z.string().nullable(), op: JournalOp })),
  total: z.number(),
  state: JournalActionState,
  /** Кто отменил эту отправку */
  revertedBy: z.object({ id: Uuid, at: z.string(), actor: JournalActor, reason: z.string() }).nullable(),
  /** Эта отправка сама — отмена: с какой причиной и не отменена ли она */
  revertOf: z.object({ id: Uuid, reason: z.string(), undone: z.boolean() }).nullable(),
  /** Предварительно: автор — человек и отправка не отменена; точный ответ даёт пробный прогон */
  revertable: z.boolean(),
  /** Курсор следующей страницы списка (передаётся в запрос как cursor) */
  cursor: z.string().optional(),
});
export type JournalAction = z.infer<typeof JournalAction>;

export const JournalActionDetail = JournalAction.extend({
  entries: z.array(JournalEntry),
  /** Записей больше, чем показано */
  truncated: z.boolean(),
});
export type JournalActionDetail = z.infer<typeof JournalActionDetail>;

const JournalDate = z.string().datetime({ offset: true });

export const JournalQuery = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(30),
  /** Курсор: отправки раньше этого времени */
  before: JournalDate.optional(),
  /** Точный курсор из ответа списка: «микросекунды» или «микросекунды.номер записи» (важнее before) */
  cursor: z
    .string()
    .regex(/^\d{1,19}(\.\d{1,19})?$/)
    .optional(),
  from: JournalDate.optional(),
  to: JournalDate.optional(),
  actorId: Uuid.optional(),
  section: JournalSectionCode.optional(),
  /** Таблица «схема.таблица» */
  table: z
    .string()
    .regex(/^[a-z_]+\.[a-z_0-9]+$/)
    .optional(),
  projectId: Uuid.optional(),
  op: JournalOp.optional(),
  /** Поиск по значениям: номер, название, текст */
  q: z.string().trim().min(2).max(100).optional(),
  /** Показывать изменения, которые портал сделал сам (фоновые задачи, обмен) */
  system: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
});
export type JournalQuery = z.infer<typeof JournalQuery>;

export const JournalAttempt = z.object({
  id: z.string(),
  at: z.string(),
  actor: JournalActor,
  method: z.string(),
  route: z.string().nullable(),
  httpStatus: z.number().nullable(),
  ip: z.string().nullable(),
  device: z.string().nullable(),
});
export type JournalAttempt = z.infer<typeof JournalAttempt>;

/** Что отменить. Всё сводится к отмене отправок целиком */
export const RevertTarget = z.discriminatedUnion('kind', [
  /** Отправки (одна или несколько выбранных) */
  z.object({ kind: z.literal('request'), keys: z.array(z.string().min(3).max(300)).min(1).max(50) }),
  /** Вернуть запись к состоянию на момент: отменяются отправки после него, которые её меняли */
  z.object({
    kind: z.literal('record'),
    schema: z.string().regex(/^[a-z_]+$/),
    table: z.string().regex(/^[a-z_0-9]+$/),
    rowId: Uuid,
    at: JournalDate,
  }),
  /** Восстановить удалённую запись: отменяется отправка, которой её удалили */
  z.object({ kind: z.literal('restore'), historyId: z.string().regex(/^\d{1,19}$/) }),
  /** Отменить отмену */
  z.object({ kind: z.literal('undo'), revertId: Uuid }),
]);
export type RevertTarget = z.infer<typeof RevertTarget>;

export const RevertPreviewInput = z.object({
  target: RevertTarget,
  /** Отменить вместе с более поздними отправками, которые меняли те же записи */
  withDependents: z.boolean().default(false),
});
export type RevertPreviewInput = z.infer<typeof RevertPreviewInput>;

export const RevertApplyInput = RevertPreviewInput.extend({
  reason: z.string().trim().min(5, 'Опишите причину — хотя бы несколько слов').max(500),
  /** Человек подтвердил всё из списка «нужно подтвердить» */
  confirm: z.boolean().default(false),
  /** Отпечаток плана из предпросмотра: отменяется ровно то, что было показано */
  planHash: z.string().min(8).max(128),
});
export type RevertApplyInput = z.infer<typeof RevertApplyInput>;

export const RevertIssue = z.object({
  code: z.string(),
  text: z.string(),
  tableTitle: z.string().nullable(),
  recordLabel: z.string().nullable(),
});
export type RevertIssue = z.infer<typeof RevertIssue>;

export const RevertRequestRef = z.object({
  key: z.string(),
  at: z.string(),
  actor: JournalActor,
  title: z.string(),
  /** Попала в план как более поздняя отправка тех же записей */
  dependent: z.boolean(),
});
export type RevertRequestRef = z.infer<typeof RevertRequestRef>;

export const RevertEffect = z.object({
  /** Что сделает отмена: I — восстановит удалённую запись, U — вернёт значения, D — удалит созданную */
  op: JournalOp,
  schema: z.string(),
  table: z.string(),
  tableTitle: z.string(),
  rowId: z.string().nullable(),
  recordLabel: z.string().nullable(),
  changes: z.array(JournalChange),
});
export type RevertEffect = z.infer<typeof RevertEffect>;

export const RevertPreview = z.object({
  /** Можно выполнить: нет того, что мешает */
  ok: z.boolean(),
  /** Есть пункты, которые нужно подтвердить */
  needsConfirm: z.boolean(),
  planHash: z.string(),
  requests: z.array(RevertRequestRef),
  /** Более поздние отправки тех же записей, без которых отмена невозможна (если не включены) */
  dependents: z.array(RevertRequestRef),
  blocks: z.array(RevertIssue),
  confirms: z.array(RevertIssue),
  notes: z.array(RevertIssue),
  effects: z.array(RevertEffect),
  /** Итоги пробного прогона, включая каскад базы */
  counts: z.object({ restored: z.number(), updated: z.number(), deleted: z.number() }),
  effectsTruncated: z.boolean(),
});
export type RevertPreview = z.infer<typeof RevertPreview>;

export const RevertResult = z.object({
  revertId: Uuid,
  counts: z.object({ restored: z.number(), updated: z.number(), deleted: z.number() }),
});
export type RevertResult = z.infer<typeof RevertResult>;

export const REVERT_KIND_LABELS = {
  request: 'Отмена отправки',
  record: 'Возврат записи к прежнему состоянию',
  restore: 'Восстановление удалённого',
  undo: 'Отмена отмены',
} as const;
export type RevertKind = keyof typeof REVERT_KIND_LABELS;

export const RevertView = z.object({
  id: Uuid,
  at: z.string(),
  actor: JournalActor,
  kind: z.enum(['request', 'record', 'restore', 'undo']),
  reason: z.string(),
  /** Сколько отправок и изменений отменено */
  requests: z.number(),
  changes: z.number(),
  title: z.string(),
  undone: z.object({ at: z.string(), actor: JournalActor, reason: z.string() }).nullable(),
  /** Ключ отправки самой отмены — по нему «Отменить отмену» и подробности */
  actionKey: z.string(),
  counts: z.object({ restored: z.number(), updated: z.number(), deleted: z.number() }),
});
export type RevertView = z.infer<typeof RevertView>;

export const DeletedRecord = z.object({
  historyId: z.string(),
  at: z.string(),
  actor: JournalActor,
  schema: z.string(),
  table: z.string(),
  tableTitle: z.string(),
  section: JournalSectionCode,
  rowId: z.string().nullable(),
  recordLabel: z.string().nullable(),
  /** Сколько связанных записей удалено той же отправкой (каскад) */
  alsoDeleted: z.number(),
  actionKey: z.string().nullable(),
  /** Курсор следующей страницы списка */
  cursor: z.string().optional(),
});
export type DeletedRecord = z.infer<typeof DeletedRecord>;

export const RecordField = z.object({
  field: z.string(),
  title: z.string().optional(),
  value: z.unknown(),
  label: z.string().nullable().optional(),
  hidden: z.boolean().optional(),
});
export type RecordField = z.infer<typeof RecordField>;

export const RecordTimeline = z.object({
  schema: z.string(),
  table: z.string(),
  tableTitle: z.string(),
  rowId: z.string(),
  recordLabel: z.string().nullable(),
  exists: z.boolean(),
  current: z.array(RecordField).nullable(),
  entries: z.array(JournalEntry),
  /** Состояние на запрошенный момент */
  stateAt: z
    .object({
      at: z.string(),
      /** existed — была; absent — ещё не была создана; deleted — была удалена к этому моменту; unknown — раньше журнала */
      state: z.enum(['existed', 'absent', 'deleted', 'unknown']),
      fields: z.array(RecordField),
    })
    .nullable(),
});
export type RecordTimeline = z.infer<typeof RecordTimeline>;

export const IntegritySeverity = z.enum(['ok', 'warn', 'fail']);
export type IntegritySeverity = z.infer<typeof IntegritySeverity>;

export const IntegrityProblem = z.object({
  code: z.string(),
  severity: z.enum(['warn', 'fail']),
  title: z.string(),
  details: z.string().nullable(),
  count: z.number().nullable(),
});
export type IntegrityProblem = z.infer<typeof IntegrityProblem>;

export const IntegrityRun = z.object({
  id: Uuid,
  at: z.string(),
  scope: z.enum(['tenant', 'platform']),
  trigger: z.string(),
  status: IntegritySeverity,
  durationMs: z.number().nullable(),
  problems: z.array(IntegrityProblem),
  /** Сколько проверок прошло */
  checks: z.number(),
});
export type IntegrityRun = z.infer<typeof IntegrityRun>;

export const IntegrityView = z.object({
  tenant: IntegrityRun.nullable(),
  /** Проверка всей базы — только владельцу портала */
  platform: IntegrityRun.nullable(),
  /** Печати журнала — только владельцу портала */
  seals: z
    .object({
      sealedDays: z.number(),
      lastDay: z.string().nullable(),
      verifiedAt: z.string().nullable(),
      broken: z.number(),
    })
    .nullable(),
  retentionMonths: z.number(),
});
export type IntegrityView = z.infer<typeof IntegrityView>;

/* ───────────────────────── Версии портала (НФ-21) ───────────────────────── */

export const VersionService = z.object({
  name: z.string(),
  deploymentId: z.string().nullable(),
  status: z.string().nullable(),
  at: z.string().nullable(),
  canRollback: z.boolean(),
});
export type VersionService = z.infer<typeof VersionService>;

export const PortalVersion = z.object({
  commit: z.string(),
  shortCommit: z.string(),
  at: z.string(),
  message: z.string().nullable(),
  current: z.boolean(),
  /** Новее работающей версии (после возврата на прошлую) */
  newer: z.boolean(),
  migrationsApplied: z.array(z.string()),
  services: z.array(VersionService),
  /** Можно переключиться на эту версию из кабинета */
  switchable: z.boolean(),
});
export type PortalVersion = z.infer<typeof PortalVersion>;

export const VersionAction = z.object({
  id: Uuid,
  at: z.string(),
  kind: z.enum(['deploy', 'rollback', 'return', 'backup']),
  status: z.string(),
  commit: z.string().nullable(),
  actor: JournalActor,
  reason: z.string().nullable(),
  details: z.record(z.string(), z.unknown()),
});
export type VersionAction = z.infer<typeof VersionAction>;

export const VersionsView = z.object({
  current: z.object({
    commit: z.string(),
    version: z.string(),
    startedAt: z.string(),
    /** Миграций в базе и в коде работающей версии; в базе больше — работает прошлая версия */
    dbMigrations: z.number(),
    codeMigrations: z.number(),
    lastMigration: z.string().nullable(),
  }),
  railway: z.object({ configured: z.boolean(), detail: z.string().nullable() }),
  backup: z.object({
    mode: z.enum(['off', 'try', 'required']),
    configured: z.boolean(),
    last: z.object({ at: z.string(), status: z.string(), detail: z.string().nullable() }).nullable(),
  }),
  versions: z.array(PortalVersion),
  actions: z.array(VersionAction),
});
export type VersionsView = z.infer<typeof VersionsView>;

export const MigrationRisk = z.object({
  tag: z.string(),
  risk: z.enum(['ok', 'warn', 'block']),
  reasons: z.array(z.string()),
});
export type MigrationRisk = z.infer<typeof MigrationRisk>;

export const VersionPlanInput = z.object({ commit: z.string().regex(/^[0-9a-f]{7,40}$/) });
export const VersionPlan = z.object({
  commit: z.string(),
  direction: z.enum(['back', 'forward', 'same']),
  /** Миграции базы новее выбранной версии: прошлая версия будет работать с ними */
  migrations: z.array(MigrationRisk),
  compatibility: z.enum(['ok', 'warn', 'block']),
  services: z.array(VersionService),
  blocks: z.array(z.string()),
  notes: z.array(z.string()),
  ok: z.boolean(),
});
export type VersionPlan = z.infer<typeof VersionPlan>;

export const VersionSwitchInput = VersionPlanInput.extend({
  reason: z.string().trim().min(5, 'Опишите причину — хотя бы несколько слов').max(500),
  /** Через сколько минут переключить: 0 — сразу, 5 — после предупреждения пользователей */
  delayMinutes: z.union([z.literal(0), z.literal(5)]).default(5),
  /** Подтверждение предупреждений о миграциях */
  confirm: z.boolean().default(false),
});
export type VersionSwitchInput = z.infer<typeof VersionSwitchInput>;

export const VersionSwitchResult = z.object({ actionId: Uuid, startsAt: z.string() });
export type VersionSwitchResult = z.infer<typeof VersionSwitchResult>;
